import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaCode, FaSearch } from 'react-icons/fa';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { toast } from 'react-toastify';

const AdminSkills = () => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    icon: '',
    proficiency: 50,
    years_experience: 0,
    featured: false
  });

  useEffect(() => {
    fetchSkills();
  }, []);

  const fetchSkills = async () => {
    try {
      const response = await apiService.getSkills();
      setSkills(response.data || []);
    } catch (error) {
      console.error('Error fetching skills:', error);
      toast.error('Failed to fetch skills');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this skill?')) {
      try {
        await apiService.deleteSkill(id);
        toast.success('Skill deleted successfully');
        fetchSkills();
      } catch (error) {
        toast.error('Failed to delete skill');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSkill) {
        await apiService.updateSkill(editingSkill.id, formData);
        toast.success('Skill updated successfully');
      } else {
        await apiService.createSkill(formData);
        toast.success('Skill created successfully');
      }
      setShowModal(false);
      setEditingSkill(null);
      setFormData({
        name: '',
        category: '',
        icon: '',
        proficiency: 50,
        years_experience: 0,
        featured: false
      });
      fetchSkills();
    } catch (error) {
      toast.error('Failed to save skill');
    }
  };

  const openEditModal = (skill) => {
    setEditingSkill(skill);
    setFormData({
      name: skill.name || '',
      category: skill.category || '',
      icon: skill.icon || '',
      proficiency: skill.proficiency || 50,
      years_experience: skill.years_experience || 0,
      featured: skill.featured || false
    });
    setShowModal(true);
  };

  const filteredSkills = skills.filter(skill =>
    skill.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    skill.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categories = [...new Set(skills.map(s => s.category))];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Skills</h1>
          <p className="text-gray-400 mt-1">Manage your technical skills</p>
        </div>
        <button
          onClick={() => {
            setEditingSkill(null);
            setFormData({
              name: '',
              category: '',
              icon: '',
              proficiency: 50,
              years_experience: 0,
              featured: false
            });
            setShowModal(true);
          }}
          className="px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg font-medium text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300 flex items-center gap-2"
        >
          <FaPlus />
          Add Skill
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search skills..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Categories */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((category) => (
            <span key={category} className="px-3 py-1 text-sm rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400">
              {category}
            </span>
          ))}
        </div>
      )}

      {/* Skills Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredSkills.map((skill) => (
          <motion.div
            key={skill.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl p-4 hover:bg-white/10 transition-all duration-300"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {skill.icon && (
                    <span className="text-xl">{skill.icon}</span>
                  )}
                  <h3 className="font-semibold text-white">{skill.name}</h3>
                </div>
                {skill.category && (
                  <span className="text-xs text-primary-400">{skill.category}</span>
                )}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => openEditModal(skill)}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-all duration-300 text-gray-400 hover:text-primary-400"
                >
                  <FaEdit size={12} />
                </button>
                <button
                  onClick={() => handleDelete(skill.id)}
                  className="p-1.5 rounded-lg hover:bg-red-500/20 transition-all duration-300 text-gray-400 hover:text-red-400"
                >
                  <FaTrash size={12} />
                </button>
              </div>
            </div>

            <div className="mt-3">
              <div className="flex items-center justify-between text-sm text-gray-400 mb-1">
                <span>Proficiency</span>
                <span>{skill.proficiency}%</span>
              </div>
              <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
                <div 
                  className="h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all duration-500"
                  style={{ width: `${skill.proficiency}%` }}
                />
              </div>
            </div>

            {skill.years_experience > 0 && (
              <p className="text-xs text-gray-500 mt-2">
                {skill.years_experience} year{skill.years_experience > 1 ? 's' : ''} experience
              </p>
            )}
          </motion.div>
        ))}
      </div>

      {filteredSkills.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-400">No skills found. Add your first skill!</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-dark-800 rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                {editingSkill ? 'Edit Skill' : 'Add Skill'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 rounded-lg hover:bg-white/10 transition-all duration-300 text-gray-400"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Skill Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., React, Python, Solidity"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Category
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., Frontend, Backend, Blockchain"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Icon (emoji)
                </label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., ⚛️, 🐍, 🔗"
                  maxLength={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Proficiency (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.proficiency}
                  onChange={(e) => setFormData({ ...formData, proficiency: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Years Experience
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.years_experience}
                  onChange={(e) => setFormData({ ...formData, years_experience: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="w-4 h-4 rounded border-white/10 bg-white/5 text-primary-500 focus:ring-primary-500"
                />
                <label className="text-gray-300">Feature this skill</label>
              </div>

              <div className="flex gap-4 pt-4 border-t border-white/10">
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg font-medium text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300"
                >
                  {editingSkill ? 'Update Skill' : 'Create Skill'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-white/10 rounded-lg font-medium text-gray-400 hover:bg-white/5 transition-all duration-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminSkills;